import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select, Button, Spin, Tag, Row, Col, Alert } from 'antd';
/* DES ICONES, ET NON DES EMOJIS. Un emoji n'est pas dessine par l'application mais par le
   systeme : son trait, sa couleur et sa taille changent d'un poste a l'autre, et il ne
   ressemble a aucune autre icone de l'ecran. */
import {
  CalculatorOutlined, CheckCircleOutlined, AimOutlined, BookOutlined, FlagOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import SinistreSearch from '../../components/SinistreSearch';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatMontant, formatDate, STATUT_DOSSIER_MAP } from '../../variables/constants';
import { toast } from 'react-toastify';

import { useTranslation, Trans } from 'react-i18next';

const { Option } = Select;
const getCurrentDossier = (dossiers = []) => [...dossiers].sort((a, b) => {
  if (Boolean(a?.estMur) !== Boolean(b?.estMur)) return a?.estMur ? -1 : 1;
  return new Date(b?.dateOuverture || 0).getTime() - new Date(a?.dateOuverture || 0).getTime();
})[0] || null;

const LigneMontant = ({ label, value, highlight }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    // padding: '8px 0', 
    borderBottom: '1px solid #f3f4f6',
    background: highlight ? '#e8f5ed' : 'transparent', borderRadius: highlight ? 6 : 0,
    padding: highlight ? '10px 12px' : '8px 0',
  }}>
    <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: highlight ? 700 : 500, color: highlight ? '#1a6b3c' : '#111827' }}>
      {formatMontant(value)}
    </span>
  </div>
);

export default function Indemnisation() {
  const { t } = useTranslation('indemnisation');
  const [searchParams] = useSearchParams();
  const [victimes, setVictimes] = useState([]);
  const [selSinistre, setSelSinistre] = useState('');
  const [selVictime, setSelVictime] = useState('');
  const [offre, setOffre] = useState(null);
  const [loadingCalc, setLoadingCalc] = useState(false);
  const [loadingVictimes, setLoadingVictimes] = useState(false);
  const [loadingContexteVictime, setLoadingContexteVictime] = useState(false);
  const [penalites, setPenalites] = useState(null);
  const [docsVictime, setDocsVictime] = useState([]);
  const [dossiersVictime, setDossiersVictime] = useState([]);

  // Présélection par query string. Les pages sinistre passent `sinistreId` ;
  // `sinistre` reste accepté pour les anciens liens.
  useEffect(() => {
    const sinistreParam = searchParams.get('sinistreId') || searchParams.get('sinistre');
    if (sinistreParam) onSinistreChange(sinistreParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onSinistreChange = async (trackingId) => {
    if (!trackingId) {
      setSelSinistre('');
      setVictimes([]);
      setSelVictime('');
      setOffre(null);
      setPenalites(null);
      setDocsVictime([]);
      setDossiersVictime([]);
      return;
    }
    setSelSinistre(trackingId);
    setSelVictime('');
    setOffre(null);
    setPenalites(null);
    setDocsVictime([]);
    setDossiersVictime([]);
    setLoadingVictimes(true);
    try {
      const res = await axiosInstance.get(`/v1/victimes/sinistre/${trackingId}`);
      setVictimes(res.data?.data || []);
    } catch {
      setVictimes([]);
    } finally { setLoadingVictimes(false); }
  };

  const calculer = async () => {
    if (!selVictime) return toast.error(t('messages.selectionnezVictime'));
    setLoadingCalc(true);
    setOffre(null);
    try {
      const res = await axiosInstance.post(`/v1/indemnisation/calculer/${selVictime}`);
      setOffre(res.data?.data);
      toast.success(t('messages.offreCalculee'));
    } catch (err) {
      toast.error(err?.response?.data?.message || t('messages.erreurCalcul'));
    } finally { setLoadingCalc(false); }
  };

  const chargerOffre = async (victimeId = selVictime) => {
    if (!victimeId) return;
    try {
      const res = await axiosInstance.get(`/v1/indemnisation/offre/victime/${victimeId}`);
      setOffre(res.data?.data);
    } catch (_) { }
  };

  const chargerContexteVictime = async (victimeId = selVictime) => {
    if (!victimeId) {
      setDocsVictime([]);
      setDossiersVictime([]);
      return;
    }
    setLoadingContexteVictime(true);
    try {
      const [docsRes, dossiersRes] = await Promise.all([
        axiosInstance.get(`/v1/ged/victimes/${victimeId}/documents`).catch(() => ({ data: { data: [] } })),
        axiosInstance.get(`/v1/dossiers-reclamation/victime/${victimeId}`).catch(() => ({ data: { data: [] } })),
      ]);
      setDocsVictime(docsRes.data?.data || []);
      setDossiersVictime(Array.isArray(dossiersRes.data?.data) ? dossiersRes.data.data : []);
    } finally {
      setLoadingContexteVictime(false);
    }
  };

  const valider = async () => {
    if (!offre) return;
    try {
      await axiosInstance.patch(`/v1/indemnisation/offre/${offre.offreTrackingId}/valider`);
      toast.success(t('messages.offreValidee'));
      chargerOffre();
    } catch (_) { toast.error(t('messages.erreur')); }
  };

  const getPenalites = async () => {
    if (!offre) return;
    try {
      const res = await axiosInstance.get(`/v1/indemnisation/penalites/${offre.offreTrackingId}`);
      setPenalites(res.data?.data);
    } catch (_) { }
  };

  const dossierCourant = getCurrentDossier(dossiersVictime);
  const statutDossier = dossierCourant?.statut
    ? (STATUT_DOSSIER_MAP[dossierCourant.statut] || { label: dossierCourant.statut, color: 'default' })
    : null;
  const progressionPieces = dossierCourant?.nbPiecesRequises
    ? Math.round(((dossierCourant.nbPiecesRecues || 0) / dossierCourant.nbPiecesRequises) * 100)
    : 0;

  return (
    <Layout title={t('titre')}>
      <div className="cb-page-header">
        <div>
          <h1 className="cb-page-title">{t('titre')}</h1>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* ── Sélection ── */}
        <Col xs={24} md={8}>
          <div className="cb-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <AimOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
              <span className="cb-card-title">{t('selection.titre')}</span>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div className="cb-form-section-title" style={{ marginBottom: 8 }}>{t('selection.sinistre')}</div>
              <SinistreSearch
                value={selSinistre || undefined}
                onChange={onSinistreChange}
                placeholder={t('selection.rechercheSinistre')}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <div className="cb-form-section-title" style={{ marginBottom: 8 }}>{t('selection.victime')}</div>
              {loadingVictimes ? <Spin size="small" /> : (
                <Select value={selVictime || undefined}
                  onChange={(v) => {
                    setSelVictime(v);
                    setOffre(null);
                    setPenalites(null);
                    chargerOffre(v);
                    chargerContexteVictime(v);
                  }}
                  style={{ width: '100%' }} placeholder={t('selection.choisirVictime')}
                  disabled={!selSinistre || !victimes.length}>
                  {victimes.map(v => (
                    <Option key={v.victimeTrackingId} value={v.victimeTrackingId}>
                      {v.nom} {v.prenoms} - <Tag color={v.typeVictime === 'DECEDE' ? 'red' : 'orange'} style={{ borderRadius: 20, fontSize: 11 }}>{v.typeVictime}</Tag>
                    </Option>
                  ))}
                  {!victimes.length && selSinistre && <Option disabled>{t('selection.aucuneVictime')}</Option>}
                </Select>
              )}
            </div>

            {selVictime && (
              <div style={{ marginBottom: 20 }}>
                {loadingContexteVictime ? <Spin size="small" /> : dossierCourant ? (
                  <>
                    <Alert
                      showIcon
                      type={dossierCourant.estMur ? 'success' : 'warning'}
                      style={{ borderRadius: 10 }}
                      message={dossierCourant.estMur ? t('dossier.mur') : t('dossier.enCollecte')}
                      description={
                        <>
                          {dossierCourant.numeroDossier ? t('dossier.numero', { numero: dossierCourant.numeroDossier }) : t('dossier.actif')}
                          {statutDossier ? ` · ${statutDossier.label}` : ''}
                          {` ${t('dossier.pieces', { recues: dossierCourant.nbPiecesRecues || 0, requises: dossierCourant.nbPiecesRequises || 0 })}`}
                        </>
                      }
                    />
                    {dossierCourant.nbPiecesRequises > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                          {t('dossier.progression')}
                        </div>
                        <div style={{ height: 8, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{
                            width: `${progressionPieces}%`,
                            height: '100%',
                            background: dossierCourant.estMur ? '#16a34a' : '#f59e0b',
                          }} />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Alert
                    showIcon
                    type="info"
                    style={{ borderRadius: 10 }}
                    message={t('dossier.aucun')}
                  />
                )}
              </div>
            )}

            <Button type="primary" icon={<CalculatorOutlined />} onClick={calculer}
              loading={loadingCalc} disabled={!selVictime} block
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, height: 44, fontSize: 15, fontWeight: 600 }}>
              {t('actions.calculer')}
            </Button>

            {offre && (
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Button block onClick={valider} icon={<CheckCircleOutlined />}
                  style={{ borderRadius: 8, borderColor: '#1a6b3c', color: '#1a6b3c' }}>
                  {t('actions.valider')}
                </Button>
                <Button block onClick={getPenalites} style={{ borderRadius: 8 }}>
                  {t('actions.calculerPenalites')}
                </Button>
              </div>
            )}

            {penalites !== null && (
              <Alert style={{ marginTop: 12, borderRadius: 8 }}
                type={penalites > 0 ? 'warning' : 'success'}
                message={t('penalites.titre', { montant: formatMontant(penalites) })}
                description={penalites > 0 ? t('penalites.applicables') : t('penalites.aucune')} />
            )}
          </div>

          {/* Guide art. CIMA */}
          <div className="cb-card" style={{ marginTop: 16, background: '#f0faf4', border: '1px solid #b7e0c9' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 600, color: '#1a6b3c', marginBottom: 10,
            }}>
              <BookOutlined /> {t('formules.titre')}
            </div>
            {[
              { art: 'Art. 258', desc: t('formules.a258') },
              { art: 'Art. 259', desc: t('formules.a259') },
              { art: 'Art. 260-a', desc: t('formules.a260a') },
              { art: 'Art. 260-b', desc: t('formules.a260b') },
              { art: 'Art. 260-c', desc: t('formules.a260c') },
              { art: 'Art. 261', desc: t('formules.a261') },
              { art: 'Art. 262', desc: t('formules.a262') },
              { art: 'Art. 263', desc: t('formules.a263') },
            ].map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 12 }}>
                <Tag color="green" style={{ borderRadius: 20, flexShrink: 0, fontSize: 10 }}>{f.art}</Tag>
                <span style={{ color: '#374151' }}>{f.desc}</span>
              </div>
            ))}
          </div>
        </Col>

        {/* ── Résultat ── */}
        <Col xs={24} md={16}>
          {selVictime && !loadingContexteVictime && (
            <div className="cb-card" style={{ marginBottom: 16 }}>
              <div className="cb-card-title" style={{ marginBottom: 12 }}>{t('ged.titre')}</div>
              {!docsVictime.length ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ borderRadius: 10 }}
                  message={t('ged.aucun')}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {docsVictime.map((doc) => (
                    <div
                      key={doc.ossanGedDocumentTrackingId || doc.ossanGedDocumentId || `${doc.titre}-${doc.dateDocument || 'na'}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '10px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: 10,
                        background: '#fff',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{doc.titre || t('ged.sansTitre')}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          {doc.dateDocument ? t('ged.datee', { date: formatDate(doc.dateDocument) }) : t('ged.sansDate')}
                        </div>
                      </div>
                      <Tag color="blue" style={{ borderRadius: 20, margin: 0 }}>
                        {(doc.typeDocument || 'AUTRES').replace(/_/g, ' ')}
                      </Tag>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {loadingCalc && (
            <div className="cb-card" style={{ textAlign: 'center', padding: 60 }}>
              <Spin size="large" />
              <p style={{ marginTop: 16, color: '#6b7280' }}>{t('resultat.calculEnCours')}</p>
            </div>
          )}

          {!loadingCalc && !offre && (
            <div className="cb-card" style={{ textAlign: 'center', padding: 80 }}>
              <CalculatorOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
              <p style={{ color: '#6b7280', fontSize: 15 }}>
                <Trans t={t} i18nKey="resultat.invite" components={[<strong />]} />
              </p>
            </div>
          )}

          {offre && !loadingCalc && (
            <div>
              {/* En-tête offre */}
              <div style={{ background: '#1a6b3c', borderRadius: 12, padding: '20px 24px', marginBottom: 20, color: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>{t('offre.montantTotal')}</div>
                    <div style={{ fontSize: 36, fontWeight: 800 }}>{formatMontant(offre.montantTotalOffre)}</div>
                    <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>
                      {t('offre.smig', { montant: formatMontant(offre.smigMensuelRetenu) })}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {offre.dateValidation
                      ? (
                        // `icon` plutot qu'un caractere colle devant le libelle : AntD
                        // l'aligne avec le texte, une coche dans la chaine non.
                        <Tag color="green" icon={<CheckCircleOutlined />}
                          style={{ borderRadius: 20, fontSize: 13, padding: '4px 12px' }}>
                          {t('offre.validee')}
                        </Tag>
                      )
                      : <Tag style={{ borderRadius: 20, fontSize: 12, padding: '4px 12px', background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}>{t('offre.enAttente')}</Tag>}
                    {offre.dateValidation && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{t('offre.le', { date: formatDate(offre.dateValidation) })}</div>}
                  </div>
                </div>
              </div>

              {/* Détail postes */}
              <div className="cb-card">
                <div className="cb-card-title" style={{ marginBottom: 16 }}>{t('offre.detailPostes')}</div>
                <LigneMontant label={t('offre.postes.fraisMedicaux')} value={offre.montantFraisMedicaux} />
                <LigneMontant label={t('offre.postes.itt')} value={offre.montantItt} />
                <LigneMontant label={t('offre.postes.physiologique')} value={offre.montantPrejPhysiologique} />
                <LigneMontant label={t('offre.postes.economique')} value={offre.montantPrejEconomique} />
                <LigneMontant label={t('offre.postes.moral')} value={offre.montantPrejMoral} />
                <LigneMontant label={t('offre.postes.tiercePersonne')} value={offre.montantTiercePersonne} />
                <LigneMontant label={t('offre.postes.pretiumDoloris')} value={offre.montantPretiumDoloris} />
                <LigneMontant label={t('offre.postes.esthetique')} value={offre.montantPrejEsthetique} />
                <LigneMontant label={t('offre.postes.carriere')} value={offre.montantPrejCarriere} />
                <LigneMontant label={t('offre.postes.scolaire')} value={offre.montantPrejScolaire} />
                <LigneMontant label={t('offre.postes.leses')} value={offre.montantPrejLeses} />
                <LigneMontant label={t('offre.postes.funeraires')} value={offre.montantFraisFuneraires} />

                <div style={{ borderTop: '2px solid #e5e7eb', margin: '12px 0' }} />

                <LigneMontant label={t('offre.postes.totalBrut')} value={offre.totalBrut} />
                <div style={{ padding: '8px 0', color: '#6b7280', fontSize: 13 }}>
                  {t('offre.tauxPartage')} <strong>{offre.tauxPartageRc}%</strong>
                </div>
                <LigneMontant label={t('offre.postes.totalNet')} value={offre.totalNet} />
                <LigneMontant label={t('offre.postes.fraisGestion')} value={offre.fraisGestion} />

                <div style={{ borderTop: '2px solid #1a6b3c', margin: '12px 0' }} />
                <LigneMontant
                  label={<><FlagOutlined /> {t('offre.postes.montantTotal')}</>}
                  value={offre.montantTotalOffre}
                  highlight
                />
              </div>
            </div>
          )}
        </Col>
      </Row>
    </Layout>
  );
}
